import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { StorefrontLayout } from '../../components/storefront/StorefrontLayout';
import { DeadlineBanner } from '../../components/storefront/DeadlineBanner';
import { Button } from '../../components/ui/Button';
import { mockStores } from '../../utils/mockData';
import { calculateStoreStatus } from '../../utils/storeStatus';
import { useCart } from '../../hooks/useCart';
import { Trash2, ShoppingBag, ArrowLeft } from 'lucide-react';
export function Cart() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    cart,
    removeFromCart,
    updateQuantity
  } = useCart();
  const store = mockStores.find(s => s.slug === slug);
  if (!store) {
    return <div className="flex items-center justify-center min-h-screen" data-id="element-1209">
        Store not found
      </div>;
  }
  const status = calculateStoreStatus(store);
  const isOpen = status === 'open' || status === 'closing-soon';
  return <StorefrontLayout storeName={store.name} storeSlug={store.slug} organizationName={store.organizationName} supportEmail={store.supportEmail} supportPhone={store.supportPhone} data-id="element-1210">
      {isOpen && <DeadlineBanner closeDate={store.settings.closeDate} isClosingSoon={status === 'closing-soon'} data-id="element-1211" />}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1212">
        <div className="flex items-center justify-between mb-8" data-id="element-1213">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-1214">Shopping Cart</h1>
          <Link to={`/store/${slug}`} className="flex items-center text-red-600 hover:text-red-700 font-medium transition-colors" data-id="element-1215">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="element-1216" />
            Continue Shopping
          </Link>
        </div>

        {cart.items.length === 0 ? <div className="text-center py-16 bg-gray-50 rounded-lg" data-id="element-1217">
            <ShoppingBag className="h-16 w-16 text-gray-400 mx-auto mb-4" data-id="element-1218" />
            <h2 className="text-xl font-medium text-gray-900 mb-2" data-id="element-1219">
              Your cart is empty
            </h2>
            <p className="text-gray-500 mb-6" data-id="element-1220">Add some items to get started!</p>
            <Link to={`/store/${slug}`} data-id="element-1221">
              <Button data-id="element-1222">Shop Now</Button>
            </Link>
          </div> : <div className="space-y-6" data-id="element-1223">
            {/* Cart Items */}
            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200" data-id="element-1224">
              {cart.items.map(item => <div key={item.id} className="p-6" data-id="element-1225">
                  <div className="flex items-start justify-between" data-id="element-1226">
                    <div className="flex-1" data-id="element-1227">
                      <h3 className="font-medium text-gray-900" data-id="element-1228">
                        {item.productName}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-1229">
                        {item.variantName}
                      </p>

                      {item.personalization && <div className="mt-2 space-y-1" data-id="element-1230">
                          {Object.entries(item.personalization).map(([key, value]) => <p key={key} className="text-xs text-gray-600" data-id="element-1231">
                                <span className="font-medium" data-id="element-1232">
                                  Personalization:
                                </span>{' '}
                                {value}
                              </p>)}
                        </div>}

                      <div className="flex items-center space-x-4 mt-4" data-id="element-1233">
                        <div className="flex items-center border border-gray-300 rounded-md" data-id="element-1234">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="px-3 py-1 hover:bg-gray-100 transition-colors" data-id="element-1235">
                            −
                          </button>
                          <span className="px-4 py-1 border-x border-gray-300" data-id="element-1236">
                            {item.quantity}
                          </span>
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="px-3 py-1 hover:bg-gray-100 transition-colors" data-id="element-1237">
                            +
                          </button>
                        </div>

                        <button onClick={() => removeFromCart(item.id)} className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center transition-colors" data-id="element-1238">
                          <Trash2 className="h-4 w-4 mr-1" data-id="element-1239" />
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="ml-6 text-right" data-id="element-1240">
                      <p className="text-lg font-bold text-gray-900" data-id="element-1241">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1242">
                        ${item.price.toFixed(2)} each
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Order Summary */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200" data-id="element-1243">
              <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1244">
                Order Summary
              </h2>
              <div className="space-y-2 mb-4" data-id="element-1245">
                <div className="flex justify-between text-gray-600" data-id="element-1246">
                  <span data-id="element-1247">Subtotal</span>
                  <span data-id="element-1248">${cart.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600" data-id="element-1249">
                  <span data-id="element-1250">Shipping</span>
                  <span data-id="element-1251">Calculated at checkout</span>
                </div>
              </div>
              <div className="border-t border-gray-300 pt-4 mb-6" data-id="element-1252">
                <div className="flex justify-between text-lg font-bold text-gray-900" data-id="element-1253">
                  <span data-id="element-1254">Total</span>
                  <span data-id="element-1255">${cart.subtotal.toFixed(2)}</span>
                </div>
              </div>

              {isOpen ? <Button onClick={() => navigate(`/store/${slug}/checkout`)} className="w-full" size="lg" data-id="element-1256">
                  Proceed to Checkout
                </Button> : <div className="text-center" data-id="element-1257">
                  <p className="text-red-600 font-medium mb-4" data-id="element-1258">
                    Store is closed - checkout unavailable
                  </p>
                  <Button disabled className="w-full opacity-50" size="lg" data-id="element-1259">
                    Checkout Unavailable
                  </Button>
                </div>}
            </div>
          </div>}
      </div>
    </StorefrontLayout>;
}